import { useT } from "@/lib/i18n";
import { useSystemStore } from "@/lib/store";
import { planRvPanelResize } from "@/lib/rv-panel-resize";
import { RV_LEGS, RV_MAX_BRANCHES, RV_PANEL_ID, rvBranchBit, rvBranchPrefix, rvPanelBranchCount, rvPanelMask } from "@/lib/catalog/rv-distribution";
import type { ComponentDefinition } from "@/lib/types";

export function RvPanelSwitches({ nodeId, def }: { nodeId: string; def: ComponentDefinition }) {
  const tr = useT();
  const state = useSystemStore(s => s.simulateState.componentStates[nodeId]);
  const terminalLabels = useSystemStore(s => {
    const node = s.system.nodes.find(n => n.id === nodeId);
    return node?.kind === "component" ? node.terminalLabels : undefined;
  });
  const setState = useSystemStore(s => s.setComponentSimulateState);
  const count = rvPanelBranchCount(def);
  if (count == null) return null;
  const mask = rvPanelMask(count, state);
  const toggle = (bit: number) => setState(nodeId, `rv:${mask ^ bit}`);
  const button = (bit: number, label: string) => <button key={bit} type="button"
    aria-label={label} title={label} aria-pressed={!!(mask & bit)} onClick={() => toggle(bit)}
    className={`max-w-full truncate rounded px-2 py-1 text-[11px] border ${mask & bit ? "bg-[var(--color-accent-soft)] border-[var(--color-accent)]" : "bg-[var(--color-surface)] border-[var(--color-border)]"}`}>
    {label}
  </button>;
  return <div className="nodrag nopan pointer-events-auto space-y-1.5 mt-2 text-center"
    onPointerDown={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
    {button(1, tr("rv_panel.main"))}
    {RV_LEGS.map(leg => <div key={leg} className="flex flex-wrap justify-center gap-1">
      {Array.from({ length: count }, (_, i) => {
        const branch = i + 1;
        const id = `${rvBranchPrefix(leg, branch)}-${leg}`;
        const circuit = `${leg.toUpperCase()} / ${branch}`;
        const label = terminalLabels?.[id] ?? def.terminals.find(t => t.id === id)?.label;
        return button(rvBranchBit(leg, branch), label?.trim() && label !== `${circuit} L` ? `${circuit} · ${label}` : circuit);
      })}
    </div>)}
    <div className="text-[10px] leading-snug text-fg-muted">{tr("rv_panel.switch_help")}</div>
  </div>;
}

export function RvPanelControls({ nodeId, def, editable }: { nodeId: string; def: ComponentDefinition; editable: boolean }) {
  const tr = useT();
  const system = useSystemStore(s => s.system);
  const mode = useSystemStore(s => s.mode);
  const customize = useSystemStore(s => s.customizeComponent);
  const count = rvPanelBranchCount(def);
  if (count == null) return null;
  return <div className="px-4 py-3 border-b border-[var(--color-border)] space-y-2">
    {def.id === RV_PANEL_ID && <><label className="flex items-center justify-between gap-2 text-[12px]">
      {tr("rv_panel.branches")}
      <select aria-label={tr("rv_panel.branches")} value={count} disabled={!editable}
        onChange={e => {
          const plan = planRvPanelResize(system, nodeId, Number(e.target.value));
          if (plan) customize(nodeId, plan);
        }} className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1">
        {Array.from({ length: RV_MAX_BRANCHES }, (_, i) => i + 1).map(n => <option key={n} value={n}
          disabled={n !== count && !planRvPanelResize(system, nodeId, n)}>{n}</option>)}
      </select>
    </label>
    <p className="text-[11px] text-fg-muted">{tr("rv_panel.resize_help")}</p></>}
    {mode === "simulate" && <RvPanelSwitches nodeId={nodeId} def={def} />}
    <p className="text-[11px] text-fg-muted">{tr("rv_panel.limits")}</p>
  </div>;
}
